import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-wrap items-start gap-4" }
const _hoisted_2 = { class: "w-72" }
const _hoisted_3 = { class: "w-48" }
const _hoisted_4 = {
  key: 1,
  class: "py-10 text-center text-gray-500 space-y-4"
}
const _hoisted_5 = {
  key: 2,
  class: "py-10 flex justify-center"
}
const _hoisted_6 = {
  key: 3,
  class: "py-10 text-center text-gray-500"
}
const _hoisted_7 = {
  key: 4,
  class: "max-h-[50vh] overflow-auto rounded-lg border border-gray-200"
}
const _hoisted_8 = { class: "w-full text-normal" }
const _hoisted_9 = { class: "sticky top-0 z-10 bg-gray-50 text-left text-gray-500" }
const _hoisted_10 = { class: "px-3 py-2 font-medium" }
const _hoisted_11 = { class: "px-3 py-2 text-right font-medium" }
const _hoisted_12 = { class: "px-3 py-2 font-medium w-40" }
const _hoisted_13 = { class: "px-3 py-2 text-right font-medium" }
const _hoisted_14 = { class: "px-3 py-2 text-right font-medium" }
const _hoisted_15 = { class: "px-3 py-2 font-medium w-56" }
const _hoisted_16 = { class: "px-3 py-2 font-medium" }
const _hoisted_17 = { class: "px-3 py-2" }
const _hoisted_18 = { class: "text-gray-500" }
const _hoisted_19 = { class: "px-3 py-2" }
const _hoisted_20 = { class: "flex items-center gap-1.5" }
const _hoisted_21 = { class: "text-sm text-gray-500" }
const _hoisted_22 = {
  key: 0,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_23 = { class: "px-3 py-2 text-right whitespace-nowrap" }
const _hoisted_24 = {
  key: 0,
  class: "italic"
}
const _hoisted_25 = {
  key: 1,
  class: "text-gray-900"
}
const _hoisted_26 = { class: "px-3 py-2 text-right" }
const _hoisted_27 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_28 = { class: "px-3 py-2" }
const _hoisted_29 = {
  key: 0,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_30 = { class: "px-3 py-2" }
const _hoisted_31 = {
  key: 0,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_32 = {
  key: 1,
  class: "pt-1 text-sm text-red-600"
}
const _hoisted_33 = {
  key: 5,
  class: "text-sm text-red-600",
  role: "alert"
}

import { computed } from 'vue';
import { useToast } from 'vue-toastification';
import { Loader2 } from 'lucide-vue-next';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select as UISelect, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Spinner from '@/components/ui/Spinner.vue';
import { bulkCountPayload, emptyEntry, evaluateBulkRow, isBulkCountEntryDirty, type BulkCountEntry, type BulkCountRowProblem } from '@/composables/inventory/bulkCount';
import { useBulkEntryDialog } from '@/composables/inventory/useBulkEntryDialog';
import { drugUnitValue, formatPercent, formatQuantity, PERCENT_TONE_CLASSES, percentTone, signedNumber } from '@/composables/inventory/inventoryTable';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineBulkCount, useSaveMedicineBulkCount } from '@/plugins/store/actions/queries/inventory';
import UnsavedInputWarning from './UnsavedInputWarning.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'BulkCountDialog',
  props: {
    "open": { type: Boolean, ...{ required: true } },
    "openModifiers": {},
  },
  emits: ["update:open"],
  setup(__props) {

const isOpen = _useModel<boolean>(__props, 'open');

const toast = useToast();
const t = (key: string, params: Record<string, unknown> = {}) => i18n.global.t(`views.health.inventory.bulkCount.${key}`, params);

// Every opening starts from a blank sheet; figures typed for a row survive a change of date, not of farm.
const {
    writableFarms,
    farmId,
    dateValue,
    dateOnly,
    todayIso,
    entries,
    serverErrors,
    generalErrors,
    data,
    rows,
    isFetching,
    isError,
    refetch,
    isSaving,
    submit,
    pendingAction,
    currentFarmName,
    requestFarm,
    requestOpen,
    confirm,
    keep,
} = useBulkEntryDialog({ isOpen, useRows: useMedicineBulkCount, useSave: useSaveMedicineBulkCount, entryFor: emptyEntry, isEntryDirty: isBulkCountEntryDirty });

const reasons = computed(() => data.value?.reasons ?? []);

const states = computed(() =>
    rows.value.flatMap((row) => {
        const entry = entries[drugUnitValue(row)];

        return entry ? [{ row, entry, state: evaluateBulkRow(row, entry) }] : [];
    })
);
const countedRows = computed(() => states.value.filter(({ state }) => !state.skipped));
const hasProblems = computed(() => countedRows.value.some(({ state }) => state.problems.length > 0));
const canSave = computed(() => farmId.value !== null && dateOnly.value !== '' && countedRows.value.length > 0 && !hasProblems.value && !isFetching.value && !isSaving.value);

function problemText(problem: BulkCountRowProblem): string {
    return t(`problems.${problem}`);
}

function reasonSelection(entry: BulkCountEntry): string | undefined {
    return entry.reasonId === null ? undefined : String(entry.reasonId);
}

function setReason(entry: BulkCountEntry, value: unknown) {
    entry.reasonId = typeof value === 'string' && value !== '' ? Number(value) : null;
}

async function save() {
    if (!canSave.value || farmId.value === null) {
        return;
    }

    const saved = await submit(bulkCountPayload(farmId.value, dateOnly.value, rows.value, entries));

    if (saved === null) {
        toast.error(t('saveFailed'));
    } else {
        toast.success(t('saved', { count: saved }));
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    "model-value": isOpen.value,
    disabled: _unref(isSaving),
    class: "sm:max-w-[1100px]",
    "onUpdate:modelValue": _unref(requestOpen)
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(t('title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(DialogDescription), { class: "mt-2 text-gray-500" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(t('description')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(CustomSelect, {
            value: _unref(farmId),
            label: _ctx.$t('views.health.inventory.table.headings.farm'),
            items: _unref(writableFarms),
            "item-text": "name",
            "item-value": "farm_id",
            required: "",
            "allow-empty": false,
            "onUpdate:value": _unref(requestFarm)
          }, null, 8, ["value", "label", "items", "onUpdate:value"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(DatePicker, {
            value: _unref(dateValue),
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (_isRef(dateValue) ? (dateValue).value = $event : null)),
            resetable: false,
            required: "",
            max: _unref(todayIso),
            label: _ctx.$t('views.health.inventory.table.headings.date')
          }, null, 8, ["value", "max", "label"])
        ])
      ]),
      (_unref(pendingAction)?.kind === 'switchFarm')
        ? (_openBlock(), _createBlock(UnsavedInputWarning, {
            key: 0,
            action: "switchFarm",
            "farm-name": _unref(currentFarmName),
            onConfirm: _unref(confirm),
            onKeep: _unref(keep)
          }, null, 8, ["farm-name", "onConfirm", "onKeep"]))
        : _createCommentVNode("", true),
      (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", null, _toDisplayString(_ctx.$t('views.pigs.records.errors.couldNotLoadData')), 1),
            _createVNode(_unref(Button), {
              variant: "outline",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(refetch)()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.errors.retry')), 1)
              ]),
              _: 1
            })
          ]))
        : (_unref(isFetching) && _unref(rows).length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(Spinner, { class: "size-6 animate-spin" })
            ]))
          : (_unref(rows).length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(t('empty')), 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("table", _hoisted_8, [
                  _createElementVNode("thead", _hoisted_9, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", _hoisted_10, _toDisplayString(_ctx.$t('views.health.inventory.table.headings.drug')), 1),
                      _createElementVNode("th", _hoisted_11, _toDisplayString(t('headings.calculated')), 1),
                      _createElementVNode("th", _hoisted_12, _toDisplayString(t('headings.counted')), 1),
                      _createElementVNode("th", _hoisted_13, _toDisplayString(t('headings.difference')), 1),
                      _createElementVNode("th", _hoisted_14, _toDisplayString(_ctx.$t('views.health.inventory.table.headings.percent')), 1),
                      _createElementVNode("th", _hoisted_15, _toDisplayString(t('headings.reason')), 1),
                      _createElementVNode("th", _hoisted_16, _toDisplayString(t('headings.note')), 1)
                    ])
                  ]),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, ({ row, entry, state }) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: _unref(drugUnitValue)(row),
                        class: _normalizeClass(["border-t border-gray-200 align-top", { 'bg-gray-50 text-gray-400': state.skipped }])
                      }, [
                        _createElementVNode("td", _hoisted_17, [
                          _createElementVNode("span", {
                            class: _normalizeClass(["font-medium", { 'text-gray-900': !state.skipped }])
                          }, _toDisplayString(row.drug_name), 3),
                          _createElementVNode("span", _hoisted_18, " (" + _toDisplayString(row.unit_name) + ")", 1)
                        ]),
                        _createElementVNode("td", {
                          class: _normalizeClass(["px-3 py-2 text-right whitespace-nowrap", { 'text-red-600': row.calculated < 0 }])
                        }, _toDisplayString(_unref(formatQuantity)(row.calculated)), 3),
                        _createElementVNode("td", _hoisted_19, [
                          _createElementVNode("div", _hoisted_20, [
                            _createVNode(_unref(Input), {
                              modelValue: entry.counted,
                              "onUpdate:modelValue": ($event: any) => ((entry.counted) = $event),
                              type: "number",
                              inputmode: "decimal",
                              min: "0",
                              step: "any",
                              autocomplete: "off",
                              class: "h-9 bg-white",
                              "aria-label": `${t('headings.counted')} ${row.drug_name}`
                            }, null, 8, ["modelValue", "onUpdate:modelValue", "aria-label"]),
                            _createElementVNode("span", _hoisted_21, _toDisplayString(row.unit_name), 1)
                          ]),
                          (state.problems.includes('invalidQuantity'))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_22, _toDisplayString(problemText('invalidQuantity')), 1))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("td", _hoisted_23, [
                          (state.skipped)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(t('skipped')), 1))
                            : (state.difference !== null)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_25, _toDisplayString(_unref(signedNumber)(state.difference, _unref(formatQuantity))), 1))
                              : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("td", _hoisted_26, [
                          (state.percent !== null)
                            ? (_openBlock(), _createBlock(_unref(Badge), {
                                key: 0,
                                variant: "outline",
                                class: _normalizeClass(["whitespace-nowrap", _unref(PERCENT_TONE_CLASSES)[_unref(percentTone)(state.percent)]])
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(formatPercent)(state.percent)), 1)
                                ]),
                                _: 2
                              }, 1032, ["class"]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_27, "-"))
                        ]),
                        _createElementVNode("td", _hoisted_28, [
                          _createVNode(_unref(UISelect), {
                            "model-value": reasonSelection(entry),
                            "onUpdate:modelValue": ($event: any) => (setReason(entry, $event))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SelectTrigger), {
                                class: _normalizeClass(["rounded-lg bg-white border-gray-200 h-9 text-normal", { 'border-red-500': state.problems.includes('reasonRequired') }]),
                                "aria-label": `${t('headings.reason')} ${row.drug_name}`
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(SelectValue), {
                                    placeholder: t('reasonPlaceholder')
                                  }, null, 8, ["placeholder"])
                                ]),
                                _: 2
                              }, 1032, ["class", "aria-label"]),
                              _createVNode(_unref(SelectContent), {
                                align: "start",
                                "body-lock": false,
                                class: "z-[2000]"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(SelectGroup), null, {
                                    default: _withCtx(() => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reasons.value, (reason) => {
                                        return (_openBlock(), _createBlock(_unref(SelectItem), {
                                          key: reason.id,
                                          value: String(reason.id)
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(reason.name), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["value"]))
                                      }), 128))
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 2
                          }, 1032, ["model-value", "onUpdate:modelValue"]),
                          (state.problems.includes('reasonRequired'))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_29, _toDisplayString(problemText('reasonRequired')), 1))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("td", _hoisted_30, [
                          _createVNode(_unref(Input), {
                            modelValue: entry.note,
                            "onUpdate:modelValue": ($event: any) => ((entry.note) = $event),
                            type: "text",
                            maxlength: "500",
                            class: _normalizeClass(["h-9 bg-white", { 'border-red-500': state.problems.includes('noteRequired') }]),
                            "aria-label": `${t('headings.note')} ${row.drug_name}`
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "aria-label"]),
                          (state.problems.includes('noteRequired'))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_31, _toDisplayString(problemText('noteRequired')), 1))
                            : _createCommentVNode("", true),
                          (_unref(serverErrors)[_unref(drugUnitValue)(row)])
                            ? (_openBlock(), _createElementBlock("div", _hoisted_32, _toDisplayString(_unref(serverErrors)[_unref(drugUnitValue)(row)]), 1))
                            : _createCommentVNode("", true)
                        ])
                      ], 2))
                    }), 128))
                  ])
                ])
              ])),
      (_unref(generalErrors).length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_33, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(generalErrors), (message) => {
              return (_openBlock(), _createElementBlock("div", { key: message }, _toDisplayString(message), 1))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (_unref(pendingAction)?.kind === 'close')
        ? (_openBlock(), _createBlock(UnsavedInputWarning, {
            key: 6,
            action: "close",
            "farm-name": _unref(currentFarmName),
            onConfirm: _unref(confirm),
            onKeep: _unref(keep)
          }, null, 8, ["farm-name", "onConfirm", "onKeep"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(DialogFooter), { class: "pt-2" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogClose), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Button), {
                variant: "outline",
                class: "min-w-32",
                disabled: _unref(isSaving)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(t('cancel')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            class: "min-w-44",
            disabled: !canSave.value && !_unref(isSaving),
            "aria-busy": _unref(isSaving),
            onClick: save
          }, {
            default: _withCtx(() => [
              (_unref(isSaving))
                ? (_openBlock(), _createBlock(_unref(Loader2), {
                    key: 0,
                    class: "size-4 animate-spin",
                    "aria-hidden": "true"
                  }))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(t('save', { count: countedRows.value.length })), 1)
            ]),
            _: 1
          }, 8, ["disabled", "aria-busy"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value", "disabled", "onUpdate:modelValue"]))
}
}

})